import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Play,
  Download,
  Zap,
  Shield,
  Activity,
  Server,
} from "lucide-react";

type CheckStatus = "pass" | "fail" | "warn" | "skip";

const StatusIcon = ({ status }: { status: CheckStatus }) => {
  switch (status) {
    case "pass": return <CheckCircle2 className="w-5 h-5 text-accent" />;
    case "fail": return <XCircle className="w-5 h-5 text-destructive" />;
    case "warn": return <AlertTriangle className="w-5 h-5 text-[hsl(40,90%,50%)]" />;
    case "skip": return <div className="w-5 h-5 rounded-full border-2 border-muted-foreground" />;
    default: return null;
  }
};

const StatusBadge = ({ status }: { status: CheckStatus }) => {
  const styles: Record<CheckStatus, string> = {
    pass: "bg-accent/10 text-accent border-accent/30",
    fail: "bg-destructive/10 text-destructive border-destructive/30",
    warn: "bg-[hsl(40,90%,50%)]/10 text-[hsl(40,90%,50%)] border-[hsl(40,90%,50%)]/30",
    skip: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span className={cn("text-xs px-2 py-0.5 border font-mono uppercase", styles[status])}>
      {status}
    </span>
  );
};

async function callDiagnostics(action: string, params?: any) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mollie-diagnostics`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, ...params }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

const checkOrder = [
  { key: "secrets", labelKey: "mollieDiag.secrets", icon: Shield },
  { key: "mollie_connectivity", labelKey: "mollieDiag.mollieConnectivity", icon: Server },
  { key: "profile_isolation", labelKey: "mollieDiag.profileIsolation", icon: Shield },
  { key: "webhooks", labelKey: "mollieDiag.webhooks", icon: Zap },
  { key: "provider_payments", labelKey: "mollieDiag.providerPayments", icon: Activity },
  { key: "subscriptions", labelKey: "mollieDiag.subscriptions", icon: Activity },
  { key: "billing_current_month", labelKey: "mollieDiag.billingCurrentMonth", icon: Activity },
  { key: "security_events", labelKey: "mollieDiag.securityEvents", icon: Shield },
  { key: "delivery_guards", labelKey: "mollieDiag.deliveryGuards", icon: Shield },
];

export default function MollieDiagnosticsPanel() {
  const { t } = useTranslation();
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);

  const { data: report, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["mollie-diagnostics"],
    queryFn: () => callDiagnostics("full_report"),
    refetchInterval: 60_000,
  });

  const simulateWebhook = useMutation({
    mutationFn: () => callDiagnostics("simulate_webhook"),
    onSuccess: (data) => {
      toast.success(t("mollieDiag.webhookSimulated", { id: data.test_payment_id }));
      refetch();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reconcilePending = useMutation({
    mutationFn: () => callDiagnostics("reconcile_pending"),
    onSuccess: (data) => {
      toast.success(t("mollieDiag.reconciled", { reconciled: data.reconciled, total: data.total }));
      refetch();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const exportAudit = useMutation({
    mutationFn: () => callDiagnostics("audit_export", { entity_type: null, limit: 500 }),
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data.logs, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("mollieDiag.auditExported", { count: data.count }));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const checks = report?.checks || {};
  const summary = report?.summary || { total: 0, passed: 0, failed: 0, warnings: 0 };
  const overallStatus: CheckStatus = summary.failed > 0 ? "fail" : summary.warnings > 0 ? "warn" : "pass";

  return (
    <div className="space-y-6">
      {/* Summary Banner */}
      <div className={cn(
        "p-6 border bg-card flex items-center justify-between",
        overallStatus === "pass" && "border-accent/30",
        overallStatus === "warn" && "border-[hsl(40,90%,50%)]/30",
        overallStatus === "fail" && "border-destructive/30",
      )}>
        <div className="flex items-center gap-4">
          <StatusIcon status={overallStatus} />
          <div>
            <h2 className="text-subheadline">
              {overallStatus === "pass" && t("mollieDiag.allPassed")}
              {overallStatus === "warn" && t("mollieDiag.warningsFound")}
              {overallStatus === "fail" && t("mollieDiag.errorsFound")}
            </h2>
            <p className="text-caption">
              {t("mollieDiag.summaryPassed", { passed: summary.passed, total: summary.total, warnings: summary.warnings, failed: summary.failed })}
            </p>
          </div>
        </div>
        <Button variant="quiet" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">{t("mollieDiag.refresh")}</span>
        </Button>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="quiet" size="sm" onClick={() => simulateWebhook.mutate()} disabled={simulateWebhook.isPending}>
          {simulateWebhook.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
          {t("mollieDiag.simulateWebhook")}
        </Button>
        <Button variant="quiet" size="sm" onClick={() => reconcilePending.mutate()} disabled={reconcilePending.isPending}>
          {reconcilePending.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {t("mollieDiag.reconcilePending")}
        </Button>
        <Button variant="quiet" size="sm" onClick={() => exportAudit.mutate()} disabled={exportAudit.isPending}>
          {exportAudit.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          {t("mollieDiag.auditExport")}
        </Button>
      </div>

      {/* Check Results */}
      {isLoading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      ) : (
        <div className="space-y-2">
          {checkOrder.map(({ key, labelKey, icon: Icon }) => {
            const check = checks[key];
            if (!check) return null;
            const isExpanded = expandedCheck === key;

            return (
              <div key={key} className="border border-border bg-card">
                <button
                  onClick={() => setExpandedCheck(isExpanded ? null : key)}
                  className="w-full flex items-center gap-4 p-4 text-left hover:bg-muted/30 transition-colors"
                >
                  <StatusIcon status={check.status} />
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <span className="flex-1 text-sm font-medium">{t(labelKey)}</span>
                  <StatusBadge status={check.status} />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border">
                    <pre className="mt-3 p-3 bg-muted/30 text-xs font-mono overflow-x-auto max-h-80 whitespace-pre-wrap">
                      {JSON.stringify(check, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* State Machine Diagram */}
      <div className="border border-border bg-card p-6 space-y-4">
        <h3 className="text-subheadline">{t("mollieDiag.stateMachineTitle")}</h3>
        <div className="text-xs font-mono text-muted-foreground space-y-6">
          <div>
            <p className="text-micro text-foreground mb-2">{t("mollieDiag.providerBillingStatus")}</p>
            <p>TRIAL → (checkout paid) → ACTIVE</p>
            <p>ACTIVE → (payment failed) → PAST_DUE</p>
            <p>PAST_DUE → (payment retry paid) → ACTIVE</p>
            <p>PAST_DUE → (7 days elapsed) → BLOCKED</p>
            <p>BLOCKED → (operator unlock + payment) → ACTIVE</p>
            <p>ACTIVE → (cancel) → CANCELED (at period end)</p>
          </div>
          <div>
            <p className="text-micro text-foreground mb-2">{t("mollieDiag.paymentSetupStatus")}</p>
            <p>none → (setup_start) → pending</p>
            <p>pending → (webhook paid) → active</p>
            <p>pending → (webhook failed/canceled/expired) → failed</p>
            <p>pending → (reconcile after 60min) → failed</p>
            <p>failed → (setup_start) → pending</p>
            <p>active → (setup_start) → pending (change method)</p>
          </div>
          <div>
            <p className="text-micro text-foreground mb-2">{t("mollieDiag.webhookProcessing")}</p>
            <p>→ received → (validate profile) → (idempotency check) → processed</p>
            <p>→ received → (profile mismatch) → SECURITY_STOP</p>
            <p>→ received → (processing error) → failed</p>
            <p>→ received → (no providerId) → ignored</p>
          </div>
        </div>
      </div>

      {/* Timestamp */}
      {report?.timestamp && (
        <p className="text-xs text-muted-foreground text-right">
          {t("mollieDiag.lastCheck")} {new Date(report.timestamp).toLocaleString()}
        </p>
      )}
    </div>
  );
}
